import { useFormikContext } from 'formik';
import constants from 'jsx/common/constants';
import get from 'lodash/get';
import {
  Condition,
  FormGroup,
  Grid,
  NestedCondition,
} from '@mss/mss-component-library';
import React from 'react';
import { Plus, Trash } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { initialCondition } from './AutomationForm';
import {
  AutomationAttribute,
  AutomationEventRange,
  AutomationStateChange,
  AutomationStateChangeOperator,
  ConditionEntityValue,
  TriggerTime,
} from './Forms';
import { Select } from './Inputs';
import classNames from 'classnames';

const cType = constants.automation.types.condition;
type NestedConditionsProps = {
  disabled?: string | boolean; // Why the ... can this be a string?
  conditions: (Condition & { conditions: any[] })[];
  index: number;
  addCondition: (index: number, nestedIndex?: number) => (e: any) => void;
  removeCondition: (index: number, nestedIndex?: number) => (e: any) => void;
};

const NestedConditions = ({
  conditions,
  index,
  addCondition,
  removeCondition,
  disabled,
}: NestedConditionsProps) => {
  return (
    <Container index={index} last={index === 0} hideBorder>
      {conditions.map((c, i) => (
        <ConditionForm
          key={c.key}
          addCondition={addCondition}
          removeCondition={removeCondition}
          condition={c}
          nestedAt={index}
          last={conditions.length - 1 === i}
          index={i}
          disabled={!!disabled}
        />
      ))}
    </Container>
  );
};

export const ConditionTypeSelector = ({
  index,
  nestedAt,
  disabled,
}: {
  index: number;
  nestedAt?: number;
  disabled: string | boolean;
}) => {
  const [t] = useTranslation(['configuration', 'common']);
  const { values, setFieldValue } = useFormikContext();
  return (
    <Select
      disabled={!!disabled}
      name={`conditions${nestedAt != null ? `.${nestedAt}.conditions` : ''}${
        index != null ? `.${index}` : ''
      }.type`}
      onChange={value => {
        const oldValue = get(values, `conditions.${index}.type`);
        setFieldValue(
          `condition${index != null ? `s.${nestedAt || index}` : ''}${
            nestedAt != null ? `.conditions.${index}` : ''
          }.settings`,
          {}
        );
        switch (value) {
          case cType.ATTRIBUTE:
          case cType.EVENT_RANGE:
          case cType.STATE_CHANGE:
          case cType.STATE_CHANGE_OPERATOR:
          case cType.ENTITY_VALUE:
          case cType.TIME:
            return nestedAt == null
              ? setFieldValue(`conditions.${index}.conditions`, undefined)
              : null;
          case cType.CONDITIONS_AND:
          case cType.CONDITIONS_OR:
            return oldValue === cType.CONDITIONS_AND ||
              oldValue === cType.CONDITIONS_OR
              ? null
              : setFieldValue(`conditions.${index}.conditions`, [
                  initialCondition(`C${index}-0`),
                ]);
          default:
            return;
        }
      }}
    >
      <Select.Option value={cType.EVENT_RANGE}>
        {t('Event range')}
      </Select.Option>
      <Select.Option value={cType.ENTITY_VALUE}>
        {t('Entity value')}
      </Select.Option>
      <Select.Option value={cType.STATE_CHANGE}>
        {t('State change')}
      </Select.Option>
      <Select.Option value={cType.STATE_CHANGE_OPERATOR}>
        {t('State change operator')}
      </Select.Option>
      <Select.Option value={cType.TIME}>{t('Time')}</Select.Option>
      <Select.Option value={cType.ATTRIBUTE}>{t('Attribute')}</Select.Option>
    </Select>
  );
};

type Props = {
  index: number;
  nestedAt?: number;
  condition: NestedCondition | (Condition & { conditions: Condition[] }); // workaround
  addCondition: (index: number, nestedIndex?: number) => (e: any) => void;
  removeCondition: (index: number, nestedIndex?: number) => (e: any) => void;
  last?: boolean;
  disabled: string | boolean;
};

const ConditionForm = ({
  index,
  condition,
  addCondition,
  removeCondition,
  last,
  nestedAt,
  disabled,
}: Props) => {
  const [t] = useTranslation(['common', 'configuration']);
  const renderForm = () => {
    switch (condition.type) {
      case cType.EVENT_RANGE:
        return (
          <AutomationEventRange
            nestedAt={nestedAt}
            index={index}
            type="condition"
            disabled={disabled}
          />
        );
      case cType.ENTITY_VALUE:
        return (
          <ConditionEntityValue
            nestedAt={nestedAt}
            index={index}
            type="condition"
            disabled={disabled}
          />
        );
      case cType.ATTRIBUTE:
        return (
          <AutomationAttribute
            nestedAt={nestedAt}
            index={index}
            type="condition"
            disabled={disabled}
          />
        );
      case cType.TIME:
        return (
          <TriggerTime
            nestedAt={nestedAt}
            index={index}
            type="condition"
            disabled={disabled}
          />
        );
      case cType.STATE_CHANGE:
        return (
          <AutomationStateChange
            nestedAt={nestedAt}
            index={index}
            type="condition"
            disabled={disabled}
          />
        );
      case cType.STATE_CHANGE_OPERATOR:
        return (
          <AutomationStateChangeOperator
            nestedAt={nestedAt}
            index={index}
            type="condition"
            disabled={disabled}
          />
        );
      case cType.CONDITIONS_AND:
      case cType.CONDITIONS_OR:
        return (
          <NestedConditions
            removeCondition={removeCondition}
            addCondition={addCondition}
            index={index}
            conditions={condition.conditions as any}
            disabled={disabled}
          />
        );
      default:
        return null;
    }
  };

  const fullWidth = () => {
    switch (condition.type) {
      case cType.CONDITIONS_AND:
      case cType.CONDITIONS_OR:
        return true;
      default:
        return false;
    }
  };

  const { values } = useFormikContext();
  return (
    <Container nestedAt={nestedAt} last={last} index={index}>
      <Actions>
        <ConditionID>
          {t('configuration:Condition ID')}:{' '}
          {get(
            values,
            `${`conditions${nestedAt != null ? `.${nestedAt}.conditions` : ''}${
              index != null ? `.${index}` : ''
            }.key`}`
          )}
        </ConditionID>
        {disabled !== 'disabled' && (
          <DeleteContainer onClick={removeCondition(index, nestedAt)}>
            <Trash size={14} color="red" />
          </DeleteContainer>
        )}
      </Actions>
      <Grid.Row>
        <Grid.Col width={4}>
          <FormGroup
            label={t('configuration:Condition type')}
            input={
              <ConditionTypeSelector
                nestedAt={nestedAt}
                index={index}
                disabled={disabled}
              />
            }
          />
        </Grid.Col>
        {fullWidth() && <Grid.Col width={8} />}
        <Grid.Col width={fullWidth() ? 12 : 8}>
          <Grid.Row>{renderForm()}</Grid.Row>
        </Grid.Col>
      </Grid.Row>
      {last && (
        <Grid.Row>
          <Grid.Col width={12}>
            <AddConditionButton
              topBorder
              nested={nestedAt != null}
              onClick={addCondition(index + 1, nestedAt)}
              data-ta="addConditionButton"
              disabled={!!disabled}
            >
              <Plus color="#96A9A7" /> {t('configuration:Add condition')}
            </AddConditionButton>
          </Grid.Col>
        </Grid.Row>
      )}
    </Container>
  );
};
type ContainerProps = {
  last?: boolean;
  index: number;
  nestedAt?: number;
  hideBorder?: boolean;
};

export const Container = ({
  className,
  last,
  nestedAt,
  index,
  hideBorder,
  ...props
}: React.ComponentProps<'div'> & ContainerProps) => {
  const borderTopWidth = (last && nestedAt != null) || index === 0 ? 0 : 10;
  const isNested = nestedAt != null;
  const marginTop = isNested && index === 0 ? 0 : 10;

  return (
    <div
      className={classNames(
        'z-0 relative w-full p-[10px]',
        {
          'border-t-[10px] border-[#eef1f0]': !hideBorder && borderTopWidth > 0,
          'border-t-0': !hideBorder && borderTopWidth === 0,
          'border-none': hideBorder,
          'mt-[10px]': isNested && marginTop > 0,
          'border-[3px] border-[#eef1f0] rounded-[5px]': isNested,
        },
        className
      )}
      {...props}
    />
  );
};

export const Actions = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={`
        h-8 -mb-[20px] 
        flex justify-end items-center 
        z-10 relative 
        ${className || ''}
      `}
      {...props}
    />
  );
};

export const DeleteContainer = ({
  className,
  ...props
}: React.ComponentProps<'div'>) => {
  return (
    <div
      className={`
        h-8 w-8 -mr-[5px] cursor-pointer 
        flex justify-center items-center 
        rounded-full hover:bg-gray-300 
        ${className || ''}
      `}
      {...props}
    />
  );
};

export const ConditionID = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => {
  return (
    <span
      className={classNames('text-[11px] bg-[#eef1f0] mr-[10px]', className)}
      {...props}
    />
  );
};

type AddConditionButtonProps = {
  topBorder?: boolean;
  nested?: boolean;
  empty?: boolean;
  disabled: string | boolean;
};

export const AddConditionButton = ({
  className,
  topBorder,
  nested,
  empty,
  disabled,
  ...props
}: React.ComponentProps<'button'> & AddConditionButtonProps) => {
  return (
    <button
      className={classNames(
        'bg-[#fbfbfb] flex items-center justify-center p-[5px] border-none text-[#96a9a7] text-[18px]',
        {
          'w-full': empty,
          'w-[calc(100%+20px)]': !empty,
          'ml-0': empty,
          'ml-[-10px]': !empty,
          'mb-0': empty,
          'mb-[-10px]': !empty,
          'border-t-[3px] border-[#eef1f0]': topBorder && nested,
          'border-t-[10px] border-[#eef1f0]': topBorder && !nested,
          'border-t-0': !topBorder,
        },
        className
      )}
      disabled={Boolean(disabled)}
      {...props}
    />
  );
};

export default ConditionForm;
